import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  CloudUpload as PushIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { useTableSort } from '../hooks/useTableSort';
import usePersistedFilters from '../hooks/usePersistedFilters';
import SortableHeaderCell from '../components/common/SortableHeaderCell';
import { formatDateTime } from '../utils/dateUtils';
import { IndicatorDialog, EMPTY_INDICATOR_OPTIONS, getIndicatorErrorMessage } from '../components/Indicators/IndicatorDialog';
import PushToEdrDialog from '../components/Indicators/PushToEdrDialog';
import { capabilitiesForType } from '../components/Integrations/agentMeta';

const confidenceColors = {
  Low: '#757575',
  Medium: '#ff9800',
  High: '#4caf50',
};

const tlpColors = {
  WHITE: '#ffffff',
  GREEN: '#4caf50',
  AMBER: '#ff9800',
  RED: '#d32f2f',
};

const statusChipColor = {
  Active: 'success',
  Inactive: 'default',
  Expired: 'warning',
  'False Positive': 'error',
};

const SEARCH_DEBOUNCE_MS = 400;
const INDICATORS_FILTER_DEFAULTS = {
  search: '', type: '', confidence: '', tlp: '', status: '', sort: '-createdAt', rowsPerPage: 20,
};

const StatCard = ({ label, value, color }) => (
  <Card>
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h4" sx={{ color }}>{value}</Typography>
    </CardContent>
  </Card>
);

export const Indicators = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermission();
  const navigate = useNavigate();

  const canCreate = hasPermission('indicators', 'create');
  const canUpdate = hasPermission('indicators', 'update');
  const canDelete = hasPermission('indicators', 'delete');

  const { filters, setFilter, reset: resetFilters } = usePersistedFilters('indicators', INDICATORS_FILTER_DEFAULTS);

  const [indicators, setIndicators] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState(EMPTY_INDICATOR_OPTIONS);
  const [stats, setStats] = useState(null);

  // Filters (server-side)
  const searchTerm = filters.search;
  const setSearchTerm = (value) => setFilter('search', value);
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm.trim());
  const typeFilter = filters.type;
  const setTypeFilter = (value) => setFilter('type', value);
  const confidenceFilter = filters.confidence;
  const setConfidenceFilter = (value) => setFilter('confidence', value);
  const tlpFilter = filters.tlp;
  const setTlpFilter = (value) => setFilter('tlp', value);
  const statusFilter = filters.status;
  const setStatusFilter = (value) => setFilter('status', value);
  const [page, setPage] = useState(0); // zero-based for TablePagination, not persisted
  const rowsPerPage = filters.rowsPerPage;
  const setRowsPerPage = (value) => setFilter('rowsPerPage', value);
  const { sort, field: sortField, direction: sortDirection, toggle: toggleSort, setSort } = useTableSort(filters.sort);

  // Keep the sort control and the persisted filters in sync in both
  // directions: adopt filters.sort when it changes externally (profile
  // hydration, "Clear filters"), otherwise persist local sort changes
  // made via column headers.
  const lastSyncedSort = useRef(filters.sort);
  useEffect(() => {
    if (sort === filters.sort) {
      lastSyncedSort.current = sort;
      return;
    }
    if (filters.sort !== lastSyncedSort.current) {
      lastSyncedSort.current = filters.sort;
      setSort(filters.sort);
    } else {
      lastSyncedSort.current = sort;
      setFilter('sort', sort);
    }
  }, [sort, filters.sort, setFilter, setSort]);

  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Bulk selection / EDR push
  const [selectedIds, setSelectedIds] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentTypes, setAgentTypes] = useState([]);
  const [pushDialogOpen, setPushDialogOpen] = useState(false);

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // Debounce the search box
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Any filter change resets to the first page
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, typeFilter, confidenceFilter, tlpFilter, statusFilter, rowsPerPage, sort]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiService.indicators.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch indicator stats:', err);
    }
  }, []);

  useEffect(() => {
    apiService.indicators.getOptions()
      .then((res) => setOptions({ ...EMPTY_INDICATOR_OPTIONS, ...res.data }))
      .catch((err) => console.error('Failed to load indicator options:', err));
    fetchStats();
  }, [fetchStats]);

  // EDR-capable agents, loaded once for the bulk "Push to EDR" action.
  useEffect(() => {
    Promise.all([apiService.agents.getAll(), apiService.agents.getTypes()])
      .then(([agentsRes, typesRes]) => {
        setAgents(agentsRes.data.agents || []);
        setAgentTypes(typesRes.data.types || []);
      })
      .catch((err) => console.error('Failed to load EDR agents:', err));
  }, []);

  const fetchIndicators = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page: page + 1, limit: rowsPerPage, sort };
      if (debouncedSearch) params.search = debouncedSearch;
      if (typeFilter) params.type = typeFilter;
      if (confidenceFilter) params.confidence = confidenceFilter;
      if (tlpFilter) params.tlp = tlpFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await apiService.indicators.getAll(params);
      setIndicators(response.data.indicators || []);
      setTotal(response.data.pagination?.total ?? (response.data.indicators || []).length);
      setSelectedIds([]);
    } catch (err) {
      console.error('Failed to fetch indicators:', err);
      if (err.response?.status !== 404) {
        setError('Failed to load indicators. Please try again.');
      } else {
        setIndicators([]);
        setTotal(0);
        setError(null);
      }
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sort, debouncedSearch, typeFilter, confidenceFilter, tlpFilter, statusFilter]);

  useEffect(() => {
    fetchIndicators();
  }, [fetchIndicators]);

  const formatIndicatorDateTime = (dateString) => formatDateTime(dateString, user?.preferences);

  const truncateValue = (value = '', maxLength = 40) =>
    value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;

  const hasFilters = Boolean(debouncedSearch || typeFilter || confidenceFilter || tlpFilter || statusFilter);

  const clearFilters = () => {
    resetFilters();
    setDebouncedSearch('');
  };

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (indicator) => {
    setEditing(indicator);
    setDialogOpen(true);
  };

  const handleSaved = (saved) => {
    notify(editing ? 'Indicator updated' : 'Indicator created');
    fetchIndicators();
    fetchStats();
    if (!editing && saved?._id) navigate(`/dashboard/indicators/${saved._id}`);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await apiService.indicators.delete(deleteTarget._id);
      notify('Indicator deleted');
      setDeleteTarget(null);
      fetchIndicators();
      fetchStats();
    } catch (err) {
      console.error('Failed to delete indicator:', err);
      notify(getIndicatorErrorMessage(err, 'Failed to delete indicator'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const overview = stats?.overview || {};
  const typeCount = Array.isArray(stats?.byType) ? stats.byType.length : 0;

  const edrEligibleCount = agents.filter((a) => a.isEnabled && capabilitiesForType(a.type, agentTypes).iocManagement).length;
  const allSelected = indicators.length > 0 && selectedIds.length === indicators.length;
  const someSelected = selectedIds.length > 0 && !allSelected;
  const toggleSelectAll = (checked) => setSelectedIds(checked ? indicators.map((i) => i._id) : []);
  const toggleSelectOne = (id, checked) => setSelectedIds((ids) => (checked ? [...ids, id] : ids.filter((x) => x !== id)));

  const handleExportCsv = async () => {
    const params = new URLSearchParams();
    Object.entries({ type: typeFilter, confidence: confidenceFilter, tlp: tlpFilter, status: statusFilter }).forEach(([k, v]) => { if (v) params.set(k, v); });
    const qs = params.toString();
    try {
      await apiService.download(`/indicators/export${qs ? `?${qs}` : ''}`, `indicators-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Indicators
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage threat indicators and IOCs
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
            Export CSV
          </Button>
          {canCreate && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New Indicator
            </Button>
          )}
        </Box>
      </Box>

      {stats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} md={3}>
            <StatCard label="Total Indicators" value={overview.total ?? 0} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Active" value={overview.active ?? 0} color="success.main" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Inactive" value={overview.inactive ?? 0} color="text.secondary" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Indicator Types" value={typeCount} />
          </Grid>
        </Grid>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button onClick={fetchIndicators} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* Filters */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search value or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                  <MenuItem value="">All Types</MenuItem>
                  {options.types.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Confidence</InputLabel>
                <Select value={confidenceFilter} label="Confidence" onChange={(e) => setConfidenceFilter(e.target.value)}>
                  <MenuItem value="">All Confidence Levels</MenuItem>
                  {options.confidences.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>TLP</InputLabel>
                <Select value={tlpFilter} label="TLP" onChange={(e) => setTlpFilter(e.target.value)}>
                  <MenuItem value="">All TLP Levels</MenuItem>
                  {options.tlp.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">All Statuses</MenuItem>
                  {options.statuses.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {canUpdate && selectedIds.length > 0 && (
            <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2, p: 1.5, bgcolor: 'action.selected', borderRadius: 1 }}>
              <Typography variant="body2">{selectedIds.length} selected</Typography>
              <Tooltip title={edrEligibleCount === 0 ? 'No enabled integration supports indicator management (CrowdStrike, SentinelOne, Microsoft Defender)' : ''}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PushIcon />}
                    onClick={() => setPushDialogOpen(true)}
                    disabled={edrEligibleCount === 0}
                  >
                    Push to EDR
                  </Button>
                </span>
              </Tooltip>
              <Button size="small" onClick={() => setSelectedIds([])}>Clear selection</Button>
            </Box>
          )}

          {/* Indicators Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someSelected}
                      checked={allSelected}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                      disabled={indicators.length === 0}
                    />
                  </TableCell>
                  <SortableHeaderCell field="type" label="Type" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="value" label="Value" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="confidence" label="Confidence" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="tlp" label="TLP" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="source" label="Source" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="status" label="Status" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="sightingCount" label="Sightings" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="createdAt" label="Created" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading indicators...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : indicators.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <Typography variant="h6" gutterBottom>
                        {hasFilters ? 'No indicators match your filters' : 'No indicators yet'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {hasFilters
                          ? 'Try adjusting or clearing your search and filters.'
                          : 'Track IPs, domains, hashes and other IOCs observed in your environment.'}
                      </Typography>
                      {hasFilters ? (
                        <Button variant="outlined" onClick={clearFilters}>Clear Filters</Button>
                      ) : canCreate ? (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                          Create your first indicator
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ) : (
                  indicators.map((indicator) => (
                    <TableRow key={indicator._id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/indicators/${indicator._id}`)}>
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.includes(indicator._id)}
                          onChange={(e) => toggleSelectOne(indicator._id, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {indicator.type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={indicator.value || ''}>
                          <Typography variant="body2" fontFamily="monospace">
                            {truncateValue(indicator.value)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={indicator.confidence || 'Unknown'}
                          size="small"
                          sx={{
                            backgroundColor: confidenceColors[indicator.confidence] || '#757575',
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={indicator.tlp || 'WHITE'}
                          size="small"
                          sx={{
                            backgroundColor: tlpColors[indicator.tlp] || tlpColors.WHITE,
                            color: !indicator.tlp || indicator.tlp === 'WHITE' ? 'black' : 'white',
                            border: !indicator.tlp || indicator.tlp === 'WHITE' ? '1px solid #ccc' : 'none',
                          }}
                        />
                      </TableCell>
                      <TableCell>{indicator.source}</TableCell>
                      <TableCell>
                        <Chip
                          label={indicator.status || 'Unknown'}
                          size="small"
                          color={statusChipColor[indicator.status] || 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={indicator.sightings?.length || 0}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>{formatIndicatorDateTime(indicator.createdAt)}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => navigate(`/dashboard/indicators/${indicator._id}`)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {canUpdate && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(indicator)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(indicator)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </CardContent>
      </Card>

      <IndicatorDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        indicator={editing}
        options={options}
        onSaved={handleSaved}
      />

      <PushToEdrDialog
        open={pushDialogOpen}
        onClose={() => setPushDialogOpen(false)}
        indicatorIds={selectedIds}
        onPushed={(data) => {
          const agentsResult = data.agents || [];
          const succeeded = agentsResult.filter((a) => a.success).length;
          notify(
            `Pushed ${data.pushed ?? selectedIds.length} indicator(s) to ${succeeded}/${agentsResult.length} agent(s)`,
            succeeded === agentsResult.length && agentsResult.length > 0 ? 'success' : 'warning'
          );
          setSelectedIds([]);
          fetchIndicators();
        }}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={deleting ? undefined : () => setDeleteTarget(null)}>
        <DialogTitle>Delete Indicator</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete the {deleteTarget?.type} indicator{' '}
            <Box component="span" fontFamily="monospace">{deleteTarget?.value}</Box>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Indicators;
